import { useCallback, useEffect, useRef, useState } from 'react';

type Direction = 1 | -1;

// Firefox reports wheel steps in lines (about 3 a notch); this makes a notch move as far as in Chrome.
const WHEEL_LINE_PX = 40;

const wheelPixels = (event: WheelEvent, rail: HTMLElement): number => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaY * WHEEL_LINE_PX;
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaY * rail.clientWidth;
  return event.deltaY;
};

interface Edges {
  readonly atStart: boolean;
  readonly atEnd: boolean;
}

interface RailScrollOptions {
  /** Turn a vertical mouse wheel into sideways scrolling, for rails with nothing to scroll vertically. */
  readonly sidewaysWheel?: boolean;
}

export const useRailScroll = ({ sidewaysWheel = false }: RailScrollOptions = {}) => {
  const [rail, setRail] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState<Edges>({ atStart: true, atEnd: true });
  const previousButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const pagedFromArrow = useRef<Direction | null>(null);

  const measure = useCallback(() => {
    if (!rail) return;
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
    setEdges((prev) => (prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }));
  }, [rail]);

  useEffect(() => {
    if (!rail) return undefined;
    const resizes = new ResizeObserver(measure);
    resizes.observe(rail);
    const items = new MutationObserver(measure);
    items.observe(rail, { childList: true });
    return () => {
      resizes.disconnect();
      items.disconnect();
    };
  }, [rail, measure]);

  useEffect(() => {
    if (!rail || !sidewaysWheel) return undefined;
    // Non-passive, or preventDefault cannot stop the page scrolling as well.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const max = rail.scrollWidth - rail.clientWidth;
      const next = Math.min(max, Math.max(0, rail.scrollLeft + wheelPixels(event, rail)));
      if (next === rail.scrollLeft) return;
      event.preventDefault();
      // Instant: a CSS smooth scroll would restart on every tick and lag behind the wheel.
      rail.scrollTo({ left: next, behavior: 'instant' });
    };
    rail.addEventListener('wheel', onWheel, { passive: false });
    return () => rail.removeEventListener('wheel', onWheel);
  }, [rail, sidewaysWheel]);

  const canPrevious = !edges.atStart;
  const canNext = !edges.atEnd;

  // An arrow unmounts at its end of the rail; a focused one would drop focus to
  // <body>, outside the dialog's focus trap, so it goes to the other arrow.
  useEffect(() => {
    const direction = pagedFromArrow.current;
    if (direction === null || (direction === 1 ? canNext : canPrevious)) return;
    pagedFromArrow.current = null;
    if (document.activeElement && document.activeElement !== document.body) return;
    (direction === 1 ? previousButtonRef : nextButtonRef).current?.focus();
  }, [canPrevious, canNext]);

  const page = (direction: Direction) => {
    if (!rail) return;
    const arrow = direction === 1 ? nextButtonRef.current : previousButtonRef.current;
    pagedFromArrow.current = arrow !== null && document.activeElement === arrow ? direction : null;
    rail.scrollBy?.({ left: direction * rail.clientWidth });
  };

  return {
    railRef: setRail,
    measure,
    canPrevious,
    canNext,
    previous: () => page(-1),
    next: () => page(1),
    previousButtonRef,
    nextButtonRef,
  };
};
